import React, { useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationOnIcon,
  Note as NoteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import AlertItem from '../../../Components/AlertItem';
import ChapterSelector from '../../../Components/ChapterSelector';
import ConfirmationDialog from '../../../Components/ConfirmationDialog';
import {
  addWorkflowChapter,
  closeWorkflowIntroduction,
  getWorkflowIntroduction,
  initiateWorkflowIntroduction,
  updateWorkflowChapter,
  WorkflowChapter,
  WorkflowDetail,
} from '../../../endpoints/introductionWorkflow';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';

const INTRO_OPEN = 'c3a81e04b6d24f0a9e7c1b5d8a2f4061';
const INTRO_CLOSED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4062';
const INTRO_INITIATED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4063';
const CHAPTER_OPEN = 'c3a81e04b6d24f0a9e7c1b5d8a2f4064';
const CHAPTER_REMOVED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4065';
const CHAPTER_ACCEPTED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4066';

const STATUS_OPTIONS = [
  { value: INTRO_OPEN, label: 'Open', color: 'info' },
  { value: INTRO_CLOSED, label: 'Closed', color: 'warning' },
  { value: INTRO_INITIATED, label: 'Initiated', color: 'success' },
];

const CHAPTER_STATUS_OPTIONS = [
  { value: CHAPTER_OPEN, label: 'Open', color: 'info' },
  { value: CHAPTER_REMOVED, label: 'Removed', color: 'error' },
  { value: CHAPTER_ACCEPTED, label: 'Accepted', color: 'success' },
];

type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

function statusColor(
  options: { value: string; color: string }[],
  statusId: string
): ChipColor {
  const match = options.find((option) => option.value === statusId);
  return (match?.color as ChipColor) || 'default';
}

function apiError(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'error' in error) {
    return String((error as { error: unknown }).error);
  }
  if (error instanceof Error) return error.message;
  return 'Request failed';
}

function formatDate(value?: string | null): string {
  if (!value) return '';
  return dayjs(value).format('MMM DD, YYYY');
}

const attemptWords = ['One', 'Two', 'Three'];

const IntroductionWorkflowDetail: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = dayjs().format('YYYY-MM-DD');

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [chapterForAction, setChapterForAction] = useState<string | null>(null);
  const [expandedActions, setExpandedActions] = useState<string | null>(null);
  const [addingChapter, setAddingChapter] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [addingAttempt, setAddingAttempt] = useState(false);
  const [attemptChapterId, setAttemptChapterId] = useState<string | null>(null);
  const [attemptNumber, setAttemptNumber] = useState<1 | 2 | 3>(1);
  const [attemptDate, setAttemptDate] = useState('');
  const [attemptSuccessful, setAttemptSuccessful] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteChapterId, setNoteChapterId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [initiationDate, setInitiationDate] = useState(today);

  const { data, isLoading, error } = useApiGet(
    ['introduction-workflow', id],
    () => getWorkflowIntroduction(id),
    { enabled: !!id }
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['introduction-workflow'],
    });
  };

  const onFail = (err: unknown) => setErrorMessage(apiError(err));
  const updateChapter = useApiSend(updateWorkflowChapter, refresh, onFail);
  const addChapter = useApiSend(
    addWorkflowChapter,
    async () => {
      setAddingChapter(false);
      setSelectedChapterId('');
      await refresh();
    },
    onFail
  );
  const closeIntro = useApiSend(
    closeWorkflowIntroduction,
    async () => {
      setShowCloseConfirm(false);
      setSuccessMessage('Introduction closed');
      await refresh();
    },
    onFail
  );
  const initiate = useApiSend(
    initiateWorkflowIntroduction,
    async () => {
      setSuccessMessage('Introduction initiated');
      await refresh();
    },
    onFail
  );

  const intro = data as WorkflowDetail | undefined;
  const busy =
    updateChapter.isPending ||
    addChapter.isPending ||
    closeIntro.isPending ||
    initiate.isPending;

  const openAttempt = (chapterId: string, number: 1 | 2 | 3) => {
    setAttemptChapterId(chapterId);
    setAttemptNumber(number);
    setAttemptDate('');
    setAttemptSuccessful(false);
    setAddingAttempt(true);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading introduction...</Typography>
      </Box>
    );
  }

  if (error || !intro) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error ? apiError(error) : 'Introduction was not found'}
        </Alert>
      </Container>
    );
  }

  const name = `${intro.first_name} ${intro.last_name}`;
  const location = [
    intro.address_1,
    intro.address_2,
    intro.city,
    intro.state_name,
    intro.postal_code,
    intro.country_name,
  ]
    .filter(Boolean)
    .join(', ');

  const renderAttempt = (
    label: string,
    when?: string | null,
    successful?: boolean | null
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
      <Typography variant="body2" sx={{ minWidth: '110px' }}>
        {label}:
      </Typography>
      {when ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(when)}
          </Typography>
          <Chip
            label={successful ? 'Reached' : 'Not Reached'}
            size="small"
            color={successful ? 'success' : 'error'}
          />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No attempt recorded
        </Typography>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/introductions')}
            sx={{ cursor: 'pointer' }}
          >
            Non-Member Intro
          </Link>
          <Typography
            color="primary.main"
            sx={
              intro.user_id
                ? {
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }
                : undefined
            }
            onClick={
              intro.user_id
                ? () => window.open(`/user/${intro.user_id}`, '_blank')
                : undefined
            }
          >
            {name}
          </Typography>
        </Breadcrumbs>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography
              variant="h4"
              component="h1"
              color="primary"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                ...(intro.user_id
                  ? {
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }
                  : {}),
              }}
              onClick={
                intro.user_id
                  ? () => window.open(`/user/${intro.user_id}`, '_blank')
                  : undefined
              }
            >
              {name}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ textAlign: { xs: 'left', md: 'right' } }}
          >
            {intro.can_edit && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<CloseIcon />}
                onClick={() => setShowCloseConfirm(true)}
                disabled={busy}
              >
                Close
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      {errorMessage && (
        <AlertItem
          severity="error"
          description={errorMessage}
          onClose={() => setErrorMessage('')}
        />
      )}
      {successMessage && (
        <AlertItem
          severity="success"
          description={successMessage}
          onClose={() => setSuccessMessage('')}
        />
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Card elevation={2}>
            <CardHeader
              title="Basic Information"
              avatar={<PersonIcon color="primary" />}
            />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={intro.status_name}
                    color={statusColor(STATUS_OPTIONS, intro.status_id)}
                    size="small"
                    sx={{ mt: 0.5, ml: 0.5 }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{intro.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Home Phone
                  </Typography>
                  <Typography variant="body2">
                    {intro.home_phone || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cell Phone
                  </Typography>
                  <Typography variant="body2">
                    {intro.cell_phone || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body2">{location || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body2">
                    {intro.created_by_name || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(intro.created_at)}
                  </Typography>
                </Box>
                {intro.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Comments
                    </Typography>
                    <Typography variant="body2">{intro.description}</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Card elevation={2}>
            <CardHeader
              title="Chapters"
              avatar={<LocationOnIcon color="primary" />}
              action={
                intro.can_add_chapter && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setAddingChapter(true)}
                    size="small"
                  >
                    Add Chapter
                  </Button>
                )
              }
            />
            <CardContent>
              {intro.chapters.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={2}>
                  No chapters assigned to this introduction.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {intro.chapters.map((chapter) => (
                    <ChapterCard
                      key={chapter.id}
                      chapter={chapter}
                      intro={intro}
                      busy={busy}
                      expanded={expandedActions === chapter.id}
                      initiationDate={initiationDate}
                      onExpand={() => setExpandedActions(chapter.id)}
                      onCollapse={() => setExpandedActions(null)}
                      onAddAttempt={openAttempt}
                      onAddNote={() => {
                        setNoteChapterId(chapter.id);
                        setNoteText('');
                        setNoteOpen(true);
                      }}
                      onAccept={() =>
                        updateChapter.mutate({
                          id,
                          chapterId: chapter.id,
                          status: 'accepted',
                        })
                      }
                      onRemove={() => {
                        setChapterForAction(chapter.id);
                        setShowRemoveConfirm(true);
                      }}
                      onInitiationDate={setInitiationDate}
                      onInitiate={() =>
                        initiate.mutate({ id, initiation_date: initiationDate })
                      }
                      renderAttempt={renderAttempt}
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={addingAttempt}
        onClose={() => setAddingAttempt(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Contact Attempt {attemptWords[attemptNumber - 1]}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="date"
              label="Attempt Date"
              value={attemptDate}
              onChange={(event) => setAttemptDate(event.target.value)}
              inputProps={{ max: today }}
              InputLabelProps={{ shrink: true }}
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={attemptSuccessful}
                  onChange={(event) =>
                    setAttemptSuccessful(event.target.checked)
                  }
                />
              }
              label="Were they able to be reached?"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setAddingAttempt(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!attemptDate || !attemptChapterId || busy}
            onClick={() => {
              if (!attemptChapterId) return;
              updateChapter.mutate(
                {
                  id,
                  chapterId: attemptChapterId,
                  contact_attempt: attemptNumber,
                  contact_on: attemptDate,
                  successful: attemptSuccessful,
                },
                { onSuccess: () => setAddingAttempt(false) }
              );
            }}
          >
            Add Attempt
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addingChapter}
        onClose={() => setAddingChapter(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Chapter to Introduction</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <ChapterSelector
              chapter={selectedChapterId}
              handleChapterSelect={(value) => setSelectedChapterId(value || '')}
              label="Select Chapter"
              all={true}
              spd=""
              keyId={id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setAddingChapter(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedChapterId || busy}
            onClick={() =>
              addChapter.mutate({ id, local_chapter_id: selectedChapterId })
            }
          >
            Add Chapter
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Note"
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setNoteOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!noteText.trim() || !noteChapterId || busy}
            onClick={() => {
              if (!noteChapterId) return;
              updateChapter.mutate(
                { id, chapterId: noteChapterId, note: noteText.trim() },
                {
                  onSuccess: () => {
                    setNoteOpen(false);
                    setNoteText('');
                  },
                }
              );
            }}
          >
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={showCloseConfirm}
        handleCancel={() => setShowCloseConfirm(false)}
        handleOk={() => closeIntro.mutate(id)}
        title="Close Introduction"
        message="Close this introduction? Chapters will stay as they are."
        confirmText="Close"
        cancelText="Cancel"
      />
      <ConfirmationDialog
        open={showRemoveConfirm}
        handleCancel={() => setShowRemoveConfirm(false)}
        handleOk={() => {
          if (!chapterForAction) return;
          updateChapter.mutate(
            { id, chapterId: chapterForAction, status: 'removed' },
            { onSuccess: () => setShowRemoveConfirm(false) }
          );
        }}
        title="Remove Chapter"
        message="Remove this chapter from the introduction?"
        confirmText="Remove"
        cancelText="Cancel"
      />
    </Container>
  );
};

function ChapterCard({
  chapter,
  intro,
  busy,
  expanded,
  initiationDate,
  onExpand,
  onCollapse,
  onAddAttempt,
  onAddNote,
  onAccept,
  onRemove,
  onInitiationDate,
  onInitiate,
  renderAttempt,
}: {
  chapter: WorkflowChapter;
  intro: WorkflowDetail;
  busy: boolean;
  expanded: boolean;
  initiationDate: string;
  onExpand: () => void;
  onCollapse: () => void;
  onAddAttempt: (chapterId: string, number: 1 | 2 | 3) => void;
  onAddNote: () => void;
  onAccept: () => void;
  onRemove: () => void;
  onInitiationDate: (value: string) => void;
  onInitiate: () => void;
  renderAttempt: (
    label: string,
    when?: string | null,
    successful?: boolean | null
  ) => React.ReactNode;
}) {
  const removed = chapter.status_id === CHAPTER_REMOVED;
  const openIntro = intro.status_id === INTRO_OPEN;
  const canAct = intro.can_edit && openIntro && !removed;
  const canStart =
    intro.can_initiate && chapter.status_id === CHAPTER_ACCEPTED && !removed;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {chapter.chapter_name}
          </Typography>
          {chapter.abbreviation && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {chapter.abbreviation}
            </Typography>
          )}
          <Chip
            label={chapter.status_name}
            color={statusColor(CHAPTER_STATUS_OPTIONS, chapter.status_id)}
            size="small"
          />
          {removed && (chapter.removed_by_name || chapter.removed_on) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {chapter.removed_by_name
                ? `Removed by ${chapter.removed_by_name}`
                : 'Removed'}
              {chapter.removed_on
                ? ` on ${formatDate(chapter.removed_on)}`
                : ''}
            </Typography>
          )}
        </Box>

        {!removed && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
              Contact Attempts:
            </Typography>
            <Stack spacing={1}>
              {renderAttempt(
                'Attempt One',
                chapter.contact_attempt_one,
                chapter.contact_one_successful
              )}
              {chapter.contact_attempt_one &&
                !chapter.contact_one_successful &&
                renderAttempt(
                  'Attempt Two',
                  chapter.contact_attempt_two,
                  chapter.contact_two_successful
                )}
              {chapter.contact_attempt_two &&
                !chapter.contact_one_successful &&
                !chapter.contact_two_successful &&
                renderAttempt(
                  'Attempt Three',
                  chapter.contact_attempt_three,
                  chapter.contact_three_successful
                )}
              {canAct && (
                <Box sx={{ pl: 2, pt: 1 }}>
                  {!chapter.contact_attempt_one && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => onAddAttempt(chapter.id, 1)}
                      disabled={busy}
                    >
                      Add Attempt One
                    </Button>
                  )}
                  {chapter.contact_attempt_one &&
                    !chapter.contact_one_successful &&
                    !chapter.contact_attempt_two && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => onAddAttempt(chapter.id, 2)}
                        disabled={busy}
                      >
                        Add Attempt Two
                      </Button>
                    )}
                  {chapter.contact_attempt_two &&
                    !chapter.contact_one_successful &&
                    !chapter.contact_two_successful &&
                    !chapter.contact_attempt_three && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => onAddAttempt(chapter.id, 3)}
                        disabled={busy}
                      >
                        Add Attempt Three
                      </Button>
                    )}
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {!removed && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Notes
              </Typography>
              {intro.can_edit && openIntro && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NoteIcon />}
                  onClick={onAddNote}
                >
                  Add Note
                </Button>
              )}
            </Box>
            {chapter.notes.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                No notes yet
              </Typography>
            ) : (
              <Stack spacing={1}>
                {chapter.notes.map((note) => (
                  <Paper key={note.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2">{note.note}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {note.created_by_name} - {formatDate(note.created_at)}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {(canAct || canStart) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            {expanded ? (
              <Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<ExpandLessIcon />}
                  onClick={onCollapse}
                  sx={{ mb: 1 }}
                >
                  Hide Actions
                </Button>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  {canAct && chapter.status_id === CHAPTER_OPEN && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      disabled={busy}
                      onClick={onAccept}
                    >
                      Accept
                    </Button>
                  )}
                  {canAct && (
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<CloseIcon />}
                      disabled={busy}
                      onClick={onRemove}
                    >
                      Remove
                    </Button>
                  )}
                  {canStart && (
                    <>
                      <TextField
                        size="small"
                        type="date"
                        label="Initiation date"
                        value={initiationDate}
                        onChange={(event) =>
                          onInitiationDate(event.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={busy || !initiationDate}
                        onClick={onInitiate}
                      >
                        Initiate
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            ) : (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<ExpandMoreIcon />}
                onClick={onExpand}
              >
                Show Actions
              </Button>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

export default IntroductionWorkflowDetail;
